import React from 'react'

import { bool, string } from 'prop-types'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import CancelConfirmationModal from './Components/CancelConfirmationModal'
import CancelSubscriptionModal from './Components/CancelSubscriptionModal/CancelSubscriptionModal'
import EmptySubscription from './Components/EmptySubscription'
import PauseConfirmationModal from './Components/PauseConfirmationModal'
import CancelInfo from './Components/Subscription/Components/CancelInfo'
import PaymentHistory from './Components/Subscription/Components/PaymentHistory'
import Subscription from './Components/Subscription/Subscription'
import SubscriptionStatusBanner from './Components/SubscriptionStatusBanner'
import SwitchPlanModal from './Components/SwitchPlanModal/SwitchPlanModal'
import { TIER } from './constants'
import useSubscriptionManagement from './hooks/useSubscriptionManagement'
import {
  paymentHistoryShape,
  plansShape,
  subscriptionPromoSectionShape,
  subscriptionShape,
} from './propTypes'

function SubscriptionManagement({
  subscription = null,
  paymentHistory = EMPTY_ARRAY,
  subscriptionPromoSection = null,
  locale = 'en',
  subscriptionV16Enabled = false,
  subscriptionPauseEnabled = false,
  plans = null,
}) {
  const {
    translate,
    subscriptionData,
    subscriptionForDisplay,
    trackingData,
    isUsageDisabled,
    feeData,
    feeError,
    isFeeLoading,
    isCancelModalOpen,
    isRestoreLoading,
    retryState,
    handleCancelClick,
    handleCloseCancel,
    handleCancelSubscription,
    handleCaptureCancellationReason,
    handleRenewClick,
    handleReactivateClick,
    handleRetryClick,
    handleCancelPlanSwitch,
    isCancelPlanSwitchLoading,
    isCancelFlowV16Enabled,
    showPauseOffer,
    handlePauseRequest,
    isPauseLoading,
    isPauseConfirmationOpen,
    handlePauseConfirmationDone,
    cancelConfirmation,
    handleCancelConfirmationDone,
    handleResumePauseClick,
    isResumeLoading,
    resumeRequested,
    resumeInProgress,
    isSwitchPlanModalOpen,
    previewData,
    handleSwitchPlanClick,
    handleCloseSwitchPlanModal,
    handleSwitchSuccess,
  } = useSubscriptionManagement({
    subscription,
    locale,
    subscriptionV16Enabled,
    subscriptionPauseEnabled,
  })

  const renderContent = () => {
    if (!subscriptionData) {
      return (
        <EmptySubscription
          locale={locale}
          promoSection={subscriptionPromoSection}
          showPremiumPlans={!!subscriptionPromoSection?.showPremiumPlans}
        />
      )
    }

    return (
      <>
        <SubscriptionStatusBanner
          subscription={subscriptionForDisplay}
          locale={locale}
          retryState={retryState}
          resumeInProgress={resumeInProgress}
        />

        <Subscription
          subscription={subscriptionForDisplay}
          locale={locale}
          isUsageDisabled={isUsageDisabled}
          onCancel={handleCancelClick}
          isCancelLoading={isFeeLoading}
          onReactivate={handleReactivateClick}
          onRenew={handleRenewClick}
          isRestoreLoading={isRestoreLoading}
          updatePaymentUrl={subscriptionData.updatePaymentUrl}
          onRetryPayment={handleRetryClick}
          retryState={retryState}
          onSwitchPlan={handleSwitchPlanClick}
          subscriptionV16Enabled={subscriptionV16Enabled}
          onCancelPlanSwitch={handleCancelPlanSwitch}
          isCancelPlanSwitchLoading={isCancelPlanSwitchLoading}
          onResumePause={handleResumePauseClick}
          isResumeLoading={isResumeLoading}
          resumeRequested={resumeRequested}
        />

        {paymentHistory && paymentHistory.length > 0 && (
          <PaymentHistory
            payments={paymentHistory}
            subscriptionId={subscriptionData.id}
            trackingData={trackingData}
            locale={locale}
          />
        )}

        {/* The v1.6 cancel flow restates the consequences inside the modal at the moment of
            intent, so the static settings block would duplicate it. Keep it for flag-off
            cohorts, which never see the consequences step. CGTrader AI keeps it per design. */}
        {(!isCancelFlowV16Enabled || subscriptionData.tier === TIER.AI_STUDIO) && (
          <CancelInfo
            locale={locale}
            subscriptionV16Enabled={subscriptionV16Enabled}
            planType={subscriptionData.planType}
            gracePeriodEligible={subscriptionData.gracePeriodEligible}
            tier={subscriptionData.tier}
          />
        )}
      </>
    )
  }

  return (
    <div className="subscription-management">
      <div className="subscription-management__header">
        <h2 className="subscription-management__title">
          {translate('subscription_management.title')}
        </h2>
        <p className="cgt-legacy-text-default">
          {translate('subscription_management.subtitle')}
        </p>
      </div>
      <div className="subscription-management__content">{renderContent()}</div>
      {subscriptionData && (
        <CancelSubscriptionModal
          isOpen={isCancelModalOpen}
          onClose={handleCloseCancel}
          onSubmit={handleCancelSubscription}
          onReasonCapture={handleCaptureCancellationReason}
          subscriptionId={subscriptionData.id}
          trackingData={trackingData}
          feeData={feeData}
          feeError={feeError}
          isFeeLoading={isFeeLoading}
          locale={locale}
          subscriptionV16Enabled={isCancelFlowV16Enabled}
          planType={subscriptionData.planType}
          gracePeriodEligible={subscriptionData.gracePeriodEligible}
          showPauseOffer={showPauseOffer}
          onPause={handlePauseRequest}
          isPauseLoading={isPauseLoading}
          tier={subscriptionData.tier}
          periodEndDate={subscriptionData.periodEndDate}
        />
      )}
      {subscriptionData && (
        <PauseConfirmationModal
          isOpen={isPauseConfirmationOpen}
          onDone={handlePauseConfirmationDone}
          pauseDate={subscriptionData.periodEndDate}
          locale={locale}
        />
      )}
      {cancelConfirmation && (
        <CancelConfirmationModal
          isOpen
          onDone={handleCancelConfirmationDone}
          accessUntil={cancelConfirmation.accessUntil}
          accessUntilLabel={cancelConfirmation.accessUntilLabel}
          cancelFee={cancelConfirmation.cancelFee}
          locale={locale}
          tier={subscriptionData?.tier}
        />
      )}
      {subscriptionData && plans && previewData && (
        <SwitchPlanModal
          isOpen={isSwitchPlanModalOpen}
          onClose={handleCloseSwitchPlanModal}
          subscription={subscriptionData}
          plans={plans}
          previewData={previewData}
          locale={locale}
          onSwitchSuccess={handleSwitchSuccess}
        />
      )}
    </div>
  )
}

SubscriptionManagement.propTypes = {
  subscription: subscriptionShape,
  paymentHistory: paymentHistoryShape,
  subscriptionPromoSection: subscriptionPromoSectionShape,
  locale: string,
  subscriptionV16Enabled: bool,
  subscriptionPauseEnabled: bool,
  plans: plansShape,
}

export default SubscriptionManagement
